// VS Code Authentication Page
// Handles authentication callback for VS Code extension
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { auth } from '../../services/firebase-config.js';
import './VSCodeAuth.css';

export default function VSCodeAuth() {
    const { user, loading } = useAuth();
    const [status, setStatus] = useState('checking');
    const [error, setError] = useState(null);
    const [callbackUrl, setCallbackUrl] = useState(null);

    useEffect(() => {
        // Get callback URL from query params
        const params = new URLSearchParams(window.location.search);
        const callback = params.get('callback');

        if (!callback) {
            setStatus('error');
            setError('No callback URL provided. Please try again from VS Code.');
            return;
        }

        // Validate it's a VS Code URI
        if (!callback.startsWith('vscode://')) {
            setStatus('error');
            setError('Invalid callback URL. Must be a VS Code URI.');
            return;
        }

        setCallbackUrl(callback);
    }, []);

    useEffect(() => {
        if (loading || !callbackUrl) return;

        if (user) {
            setStatus('ready');
        } else {
            setStatus('needs-signin');
        }
    }, [user, loading, callbackUrl]);

    const handleOpenInVSCode = async () => {
        if (!user || !callbackUrl) return;

        setStatus('sending');

        try {
            // Get fresh ID token
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) {
                throw new Error('Not signed in');
            }

            const idToken = await firebaseUser.getIdToken(true);

            // Build the callback URL with auth data
            const url = new URL(callbackUrl);
            url.searchParams.set('token', idToken);
            url.searchParams.set('uid', user.uid);
            url.searchParams.set('email', user.email || '');
            if (user.displayName) {
                url.searchParams.set('displayName', user.displayName);
            }

            // Redirect to VS Code
            setStatus('redirecting');
            window.location.href = url.toString();

        } catch (err) {
            console.error('Failed to get token:', err);
            setStatus('error');
            setError(`Failed to get authentication token: ${err.message}`);
        }
    };

    const handleSignIn = () => {
        // Redirect to login with callback preserved
        const loginUrl = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        window.location.href = loginUrl;
    };

    if (loading || status === 'checking') {
        return (
            <div className="vscode-auth-page">
                <div className="vscode-auth-container">
                    <div className="vscode-auth-icon">
                        <VSCodeIcon />
                    </div>
                    <h1>VS Code Authentication</h1>
                    <div className="loading-spinner"></div>
                    <p>Checking authentication status...</p>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="vscode-auth-page">
                <div className="vscode-auth-container error">
                    <div className="error-icon">✗</div>
                    <h1>Authentication Failed</h1>
                    <p className="error-message">{error}</p>
                    <a href="/" className="btn-secondary">Go to Home</a>
                </div>
            </div>
        );
    }

    if (status === 'needs-signin') {
        return (
            <div className="vscode-auth-page">
                <div className="vscode-auth-container">
                    <div className="vscode-auth-icon">
                        <VSCodeIcon />
                    </div>
                    <h1>Sign In Required</h1>
                    <p>Please sign in to connect your Page Notes account to VS Code.</p>
                    <button onClick={handleSignIn} className="btn-primary">
                        Sign In with Google
                    </button>
                </div>
            </div>
        );
    }

    if (status === 'redirecting' || status === 'sending') {
        return (
            <div className="vscode-auth-page">
                <div className="vscode-auth-container">
                    <div className="vscode-auth-icon">
                        <VSCodeIcon />
                    </div>
                    <h1>Opening VS Code...</h1>
                    <div className="loading-spinner"></div>
                    <p>If VS Code doesn't open automatically, please try again.</p>
                </div>
            </div>
        );
    }

    // Ready state
    return (
        <div className="vscode-auth-page">
            <div className="vscode-auth-container">
                <div className="vscode-auth-icon">
                    <VSCodeIcon />
                </div>
                <h1>Connect to VS Code</h1>
                <div className="user-info">
                    {user.photoURL && (
                        <img src={user.photoURL} alt="" className="user-avatar" />
                    )}
                    <div>
                        <div className="user-name">{user.displayName}</div>
                        <div className="user-email">{user.email}</div>
                    </div>
                </div>
                <p>Click the button below to open VS Code and complete the sign-in process.</p>
                <button onClick={handleOpenInVSCode} className="btn-primary btn-vscode">
                    <VSCodeIcon small />
                    Open in VS Code
                </button>
                <p className="note">
                    This will securely send your authentication token to the VS Code Notes extension.
                </p>
            </div>
        </div>
    );
}

function VSCodeIcon({ small }) {
    return (
        <svg 
            width={small ? "20" : "64"} 
            height={small ? "20" : "64"} 
            viewBox="0 0 100 100" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
        >
            <path 
                d="M74.5 96.8L26 78.6l-20.3-13V34.4l20.3-13L74.5 3.2l19.8 7.9v78l-19.8 7.7z" 
                fill="#007ACC"
            />
            <path 
                d="M74.5 96.8l-48.5-18.2-20.3-13V34.4l20.3-13L74.5 3.2v93.6z" 
                fill="#1f9cf0"
            />
            <path 
                d="M74.5 3.2v93.6l19.8-7.7V11.1L74.5 3.2z" 
                fill="#0065A9"
            />
            <path 
                d="M50 29.7L26 52.7l24 23v-15l-12-8 12-8v-15z" 
                fill="white"
            />
            <path 
                d="M74.5 29.7l-24 23 24 23v-15l-12-8 12-8v-15z" 
                fill="white"
            />
        </svg>
    );
}
